'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useEffect, useRef } from 'react';
import {
  LuArrowRight,
  LuCheck,
  LuTerminal,
  LuBrain,
  LuLock,
  LuGitBranch,
  LuFileCode,
  LuSparkles,
  LuServer,
  LuBox,
  LuChevronRight,
} from 'react-icons/lu';
import { Button } from '@/core/ui/primitives/button';
import { cn } from '@/core/helpers/misc/GeneralUtils';
import { useTheme } from '@/core/helpers/react/hooks/useTheme';
import { useFullWidthLayout } from '@/core/helpers/react/contexts/LayoutModeContext';
import { useAuthControls } from '@/core/ui/auth/useAuthControls';
import { useProfileOrganizations } from '@/core/ui/profile/ProfileOrganizationProvider';

export default function WelcomePage() {
  const [activeTab, setActiveTab] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const { isDark: isDarkMode } = useTheme();
  const pathname = usePathname();
  const lastPathnameRef = useRef<string | null>(null);
  const { signIn, status, user } = useAuthControls();
  const { currentOrganization } = useProfileOrganizations();
  const isAuthenticated = status === 'authenticated' && !!user;
  const firstVault = currentOrganization?.vaults?.[0];
  const vaultsHref = firstVault?.href ?? (currentOrganization?.slug ? `/${currentOrganization.slug}` : '/inovisum');

  // Request full-width layout (no max-width constraint)
  useFullWidthLayout();

  // Replay animations when navigating to this page
  useEffect(() => {
    const triggerAnimation = () => {
      setIsVisible(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    };

    // Initial mount or navigation to this page
    const isNewNavigation = lastPathnameRef.current !== null && lastPathnameRef.current !== pathname;
    lastPathnameRef.current = pathname;

    if (isNewNavigation) {
      triggerAnimation();
    } else {
      setIsVisible(true);
    }

    // Listen for hashchange to replay animation when clicking anchor links
    const handleHashChange = () => {
      triggerAnimation();
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [pathname]);

  // ============================================================================
  // THEME PALETTE - Light/Dark mode support (from layout2)
  // ============================================================================
  const theme = {
    // === BACKGROUNDS ===
    bg: isDarkMode ? 'bg-[#0a0a0b]' : 'bg-[#fafafa]',
    bgPanel: isDarkMode ? 'bg-[#0d0d0e]' : 'bg-white',
    cardBg: isDarkMode ? 'bg-white/5' : 'bg-gray-100/80',
    sectionBgAlt: isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-200/70',
    codeBg: isDarkMode ? 'bg-[#1a1a1c]' : 'bg-gray-900',

    // === TEXT COLORS ===
    text: isDarkMode ? 'text-white' : 'text-gray-900',
    textBody: isDarkMode ? 'text-white/80' : 'text-gray-700',
    textMuted: isDarkMode ? 'text-white/60' : 'text-gray-500',
    textSubtle: isDarkMode ? 'text-white/40' : 'text-gray-400',
    textDim: isDarkMode ? 'text-white/30' : 'text-gray-300',

    // === BORDERS ===
    border: isDarkMode ? 'border-white/10' : 'border-gray-200',
    borderSubtle: isDarkMode ? 'border-white/5' : 'border-gray-100',
    divider: isDarkMode ? 'bg-white/10' : 'bg-gray-200',

    // === HOVER/ACTIVE STATES ===
    hoverText: isDarkMode ? 'hover:text-white' : 'hover:text-gray-900',

    // === BUTTON STYLES ===
    buttonOutline: isDarkMode
      ? 'bg-white/5 border-white/10 text-white/60 hover:text-white hover:bg-white/10 hover:border-white/20'
      : 'bg-gray-50 border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-100 hover:border-gray-300',
    buttonPrimary: isDarkMode ? 'bg-white text-black hover:bg-white/90' : 'bg-gray-900 text-white hover:bg-gray-800',

    // === TERMINAL STYLES ===
    terminalText: isDarkMode ? 'text-white/70' : 'text-gray-300',
    terminalTextStrong: isDarkMode ? 'text-white' : 'text-white',
    terminalBorder: isDarkMode ? 'border-white/10' : 'border-gray-700',
  };

  return (
    <main className={cn('min-h-screen overflow-x-hidden font-sans transition-colors duration-300 pt-16', theme.text)}>
      {/* Hero Section */}
      <section id='welcome' className='relative wide:pt-32 desktop:pt-12 pt-0 pb-24 px-6 lg:px-8 scroll-mt-20'>
        <div className='max-w-6xl mx-auto'>
          {/* Badge */}
          <div
            className={cn(
              'flex justify-center mb-8 transition-all duration-1000',
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            )}>
            <div
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border',
                theme.cardBg,
                theme.border
              )}>
              <span className='flex h-2 w-2'>
                <span className='animate-ping absolute inline-flex h-2 w-2 rounded-full bg-brand opacity-75' />
                <span className='relative inline-flex rounded-full h-2 w-2 bg-brand' />
              </span>
              <span className={theme.textMuted}>The first docs platform with native MCP support</span>
            </div>
          </div>

          {/* Main headline */}
          <h1
            className={cn(
              'text-center text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-8 transition-all duration-1000 delay-100',
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            )}>
            <span className={cn('block', theme.textBody)}>Documentation</span>
            <span className='block bg-gradient-to-r from-primary via-brand to-primary bg-clip-text text-transparent'>
              your AI can read
            </span>
          </h1>

          {/* Subheadline */}
          <p
            className={cn(
              'text-center text-xl sm:text-2xl max-w-3xl mx-auto mb-12 leading-relaxed transition-all duration-1000 delay-200',
              theme.textMuted,
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            )}>
            Your architects write. Your AI implements.
            <br className='hidden sm:block' />
            <span className={theme.textBody}>The bridge between human knowledge and AI action.</span>
          </p>

          {/* CTA Buttons */}
          <div
            className={cn(
              'flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 transition-all duration-1000 delay-300',
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            )}>
            {isAuthenticated ? (
              <Link href={vaultsHref}>
                <button
                  className={cn(
                    'flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all group',
                    theme.buttonPrimary
                  )}>
                  Go to your vaults
                  <LuArrowRight className='h-4 w-4 group-hover:translate-x-1 transition-transform' />
                </button>
              </Link>
            ) : (
              <button
                onClick={() => signIn()}
                className={cn(
                  'flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all group',
                  theme.buttonPrimary
                )}>
                Start free during beta
                <LuArrowRight className='h-4 w-4 group-hover:translate-x-1 transition-transform' />
              </button>
            )}
            <Link href='#how-it-works'>
              <button
                className={cn(
                  'flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all border',
                  theme.buttonOutline
                )}>
                See how it works
              </button>
            </Link>
          </div>

          {/* Code preview - Terminal always stays dark for aesthetic */}
          <div
            className={cn(
              'relative max-w-4xl mx-auto transition-all duration-1000 delay-500',
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}>
            {/* Glow effect */}
            <div className='absolute -inset-4 bg-gradient-to-r from-primary/20 via-brand/20 to-primary/20 rounded-2xl blur-2xl opacity-50' />

            {/* Terminal window - always dark */}
            <div className='relative rounded-xl border border-white/10 bg-[#0d0d0e] shadow-2xl overflow-hidden'>
              {/* Window chrome */}
              <div className='flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-white/[0.02]'>
                <div className='flex gap-2'>
                  <div className='w-3 h-3 rounded-full bg-[#ff5f57]' />
                  <div className='w-3 h-3 rounded-full bg-[#febc2e]' />
                  <div className='w-3 h-3 rounded-full bg-[#28c840]' />
                </div>
                <div className='flex-1 text-center'>
                  <span className='text-xs text-white/30 font-mono'>cursor — architect-docs</span>
                </div>
              </div>

              {/* Code content */}
              <div className='p-6 font-mono text-sm leading-relaxed'>
                <div className='flex items-start gap-3 mb-3'>
                  <span className='text-gray-500 select-none'>{'>'}</span>
                  <span className='text-brand'>Read the authentication architecture from Inovisum</span>
                </div>
                <div className='pl-6 text-gray-400 mb-4'>
                  <span className='text-brand'>⟳</span> Connecting to MCP server...
                </div>
                <div className='pl-6 space-y-1 text-gray-300'>
                  <div>
                    <span className='text-brand'>✓</span> Found:{' '}
                    <span className='text-white'>/architecture/auth-flow.md</span>
                  </div>
                  <div>
                    <span className='text-brand'>✓</span> Found:{' '}
                    <span className='text-white'>/specs/oauth-implementation.md</span>
                  </div>
                  <div>
                    <span className='text-brand'>✓</span> Found:{' '}
                    <span className='text-white'>/decisions/ADR-042-jwt-strategy.md</span>
                  </div>
                </div>
                <div className='mt-4 pt-4 border-t border-white/5'>
                  <div className='flex items-start gap-3'>
                    <span className='text-gray-500 select-none'>{'>'}</span>
                    <span className='text-brand'>
                      Implement the OAuth flow following the documented patterns
                      <span className='animate-pulse'>▊</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className={cn('w-full h-px', theme.divider)} />

      {/* The Workflow Section */}
      <section id='how-it-works' className='py-24 px-6 lg:px-8 scroll-mt-20'>
        <div className='max-w-6xl mx-auto'>
          <div className='text-center mb-16'>
            <div
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs mb-6 border',
                theme.cardBg,
                theme.border,
                theme.textMuted
              )}>
              <LuSparkles className='h-3 w-3 text-brand' />
              THE WORKFLOW
            </div>
            <h2 className='text-4xl sm:text-5xl font-bold tracking-tight mb-4'>
              From architect's mind
              <br />
              <span className={theme.textMuted}>to developer's code</span>
            </h2>
          </div>

          {/* Flow visualization */}
          <div className='relative'>
            {/* Connection lines */}
            <div
              className={cn(
                'absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent to-transparent hidden lg:block',
                isDarkMode ? 'via-white/10' : 'via-gray-200'
              )}
            />

            <div className='grid lg:grid-cols-3 gap-8 lg:gap-4'>
              {/* Step 1: Architect */}
              <FlowCard
                step='01'
                icon={<LuFileCode className='h-6 w-6' />}
                title='Architect Documents'
                description='Write specs, ADRs, and architecture decisions in your vault. Native diagrams, rich formatting, living documentation.'
                gradient='from-violet-500/20 to-purple-500/20'
                iconColor='text-violet-400'
                isDarkMode={isDarkMode}
              />

              {/* Step 2: MCP Bridge */}
              <FlowCard
                step='02'
                icon={<LuBrain className='h-6 w-6' />}
                title='AI Reads via MCP'
                description='Cursor, Claude, Copilot connect directly to your docs. No copy-paste. No stale context. Always current.'
                gradient='from-primary/20 to-brand/20'
                iconColor='text-brand'
                highlight
                isDarkMode={isDarkMode}
              />

              {/* Step 3: Developer */}
              <FlowCard
                step='03'
                icon={<LuTerminal className='h-6 w-6' />}
                title='Developer Ships'
                description='AI implements following your documented patterns. Code matches architecture. Teams stay aligned.'
                gradient='from-blue-500/20 to-cyan-500/20'
                iconColor='text-blue-400'
                isDarkMode={isDarkMode}
              />
            </div>
          </div>

          {/* Workflow quote */}
          <div className='mt-16 text-center'>
            <blockquote className={cn('text-xl sm:text-2xl italic max-w-3xl mx-auto', theme.textMuted)}>
              "The architect's documentation becomes the AI's implementation guide.
              <br />
              <span className={theme.textBody}>Finally, specs that actually get followed."</span>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id='features' className={cn('py-24 px-6 lg:px-8 scroll-mt-20', theme.sectionBgAlt)}>
        <div className='max-w-6xl mx-auto'>
          <div className='text-center mb-16'>
            <div
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs mb-6 border',
                theme.cardBg,
                theme.border,
                theme.textMuted
              )}>
              <LuBox className='h-3 w-3 text-brand' />
              DIFFERENTIATORS
            </div>
            <h2 className='text-4xl sm:text-5xl font-bold tracking-tight mb-4'>
              What makes us
              <br />
              <span className={theme.textMuted}>impossible to ignore</span>
            </h2>
          </div>

          {/* Feature tabs */}
          <div className='flex flex-wrap justify-center gap-2 mb-12'>
            {[
              { label: 'MCP Native', icon: LuBrain },
              { label: 'Local LLMs', icon: LuLock },
              { label: '28 Diagrams', icon: LuFileCode },
              { label: 'Git Owned', icon: LuGitBranch },
            ].map((tab, i) => (
              <button
                key={tab.label}
                onClick={() => setActiveTab(i)}
                className={cn(
                  'flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all',
                  activeTab === i ? theme.buttonPrimary : theme.buttonOutline
                )}>
                <tab.icon className='h-4 w-4' />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Feature content */}
          <div className='relative min-h-[400px]'>
            {/* MCP Native */}
            <FeaturePanel active={activeTab === 0}>
              <div className='grid lg:grid-cols-2 gap-12 items-center'>
                <div>
                  <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/10 border border-brand/20 text-xs text-brand mb-6'>
                    ONLY PLATFORM WITH THIS
                  </div>
                  <h3 className='text-3xl sm:text-4xl font-bold mb-6'>Native MCP Support</h3>
                  <p className={cn('text-lg mb-8 leading-relaxed', theme.textMuted)}>
                    Model Context Protocol is how AI agents read external data. We're the only documentation platform
                    with first-class MCP support. Your Cursor, Claude, or custom agents can read and write your docs
                    natively.
                  </p>
                  <ul className='space-y-4'>
                    {[
                      'AI reads your architecture specs directly',
                      'Agents write drafts for review',
                      '@AI comments for async document review',
                      'Zero copy-paste, always in sync',
                    ].map((item) => (
                      <li key={item} className={cn('flex items-center gap-3', theme.textBody)}>
                        <LuCheck className='h-5 w-5 text-brand shrink-0' />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className='relative'>
                  <div className='absolute -inset-4 bg-gradient-to-r from-brand/10 to-brand/10 rounded-2xl blur-2xl' />
                  <div
                    className={cn(
                      'relative rounded-xl border p-6 font-mono text-sm',
                      theme.codeBg,
                      theme.terminalBorder
                    )}>
                    <div className={theme.textDim}>{'// mcp-config.json'}</div>
                    <pre className={theme.terminalText}>
                      {`{
  "servers": {
    "inovisum": {
      "url": "https://your-org.inovisum.io",
      "vault": "architecture",
      `}
                      <span className='text-brand'>"capabilities"</span>
                      {`: [
        "read",
        "write",
        "search"
      ]
    }
  }
}`}
                    </pre>
                  </div>
                </div>
              </div>
            </FeaturePanel>

            {/* Local LLMs */}
            <FeaturePanel active={activeTab === 1}>
              <div className='grid lg:grid-cols-2 gap-12 items-center'>
                <div>
                  <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/10 border border-brand/20 text-xs text-brand mb-6'>
                    DATA SOVEREIGNTY
                  </div>
                  <h3 className='text-3xl sm:text-4xl font-bold mb-6'>Your LLMs. Your Data.</h3>
                  <p className={cn('text-lg mb-8 leading-relaxed', theme.textMuted)}>
                    Not everyone can send their proprietary architecture to OpenAI. Connect your own Ollama, vLLM, or
                    any local model. Semantic search, AI features, zero cloud exposure.
                  </p>
                  <ul className='space-y-4'>
                    {[
                      'Connect Ollama or any OpenAI-compatible endpoint',
                      'Semantic search with your own embeddings',
                      'AI features without data leaving your infra',
                      'Perfect for regulated industries',
                    ].map((item) => (
                      <li key={item} className={cn('flex items-center gap-3', theme.textBody)}>
                        <LuLock className='h-5 w-5 text-brand shrink-0' />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className='relative'>
                  <div className='absolute -inset-4 bg-gradient-to-r from-brand/10 to-cyan-500/10 rounded-2xl blur-2xl' />
                  <div className='relative grid grid-cols-2 gap-4'>
                    <SecurityCard
                      icon={<LuServer className='h-8 w-8' />}
                      label='Self-hosted models'
                      isDarkMode={isDarkMode}
                    />
                    <SecurityCard
                      icon={<LuLock className='h-8 w-8' />}
                      label='Zero cloud exposure'
                      isDarkMode={isDarkMode}
                    />
                    <SecurityCard
                      icon={<LuGitBranch className='h-8 w-8' />}
                      label='You own the repo'
                      isDarkMode={isDarkMode}
                    />
                    <SecurityCard
                      icon={<LuBrain className='h-8 w-8' />}
                      label='Local embeddings'
                      isDarkMode={isDarkMode}
                    />
                  </div>
                </div>
              </div>
            </FeaturePanel>

            {/* 28 Diagrams */}
            <FeaturePanel active={activeTab === 2}>
              <div className='grid lg:grid-cols-2 gap-12 items-center'>
                <div>
                  <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 text-xs text-violet-400 mb-6'>
                    TEXT-TO-DIAGRAM
                  </div>
                  <h3 className='text-3xl sm:text-4xl font-bold mb-6'>28 Diagram Formats</h3>
                  <p className={cn('text-lg mb-8 leading-relaxed', theme.textMuted)}>
                    Native support for Mermaid, PlantUML, D2, Excalidraw, GraphViz, and 23 more via Kroki integration.
                    Your diagrams live in version control, render live, and never go stale.
                  </p>
                  <div className='flex flex-wrap gap-2'>
                    {['Mermaid', 'PlantUML', 'D2', 'Excalidraw', 'GraphViz', 'BPMN', 'C4', 'ERD', '+20 more'].map(
                      (d) => (
                        <span
                          key={d}
                          className={cn(
                            'px-3 py-1 rounded-full text-sm border',
                            theme.cardBg,
                            theme.border,
                            theme.textBody
                          )}>
                          {d}
                        </span>
                      )
                    )}
                  </div>
                </div>
                <div className='relative'>
                  <div className='absolute -inset-4 bg-gradient-to-r from-violet-500/10 to-purple-500/10 rounded-2xl blur-2xl' />
                  <div
                    className={cn(
                      'relative rounded-xl border p-6 font-mono text-sm',
                      theme.codeBg,
                      theme.terminalBorder
                    )}>
                    <div className={cn('mb-3', theme.textDim)}>```mermaid</div>
                    <pre className='text-violet-300'>
                      {`sequenceDiagram
    Architect->>Inovisum: Write specs
    Developer->>Cursor: "Read auth flow"
    Cursor->>Inovisum: MCP request
    Inovisum-->>Cursor: Architecture docs
    Cursor->>Developer: Implements pattern`}
                    </pre>
                    <div className={cn('mt-3', theme.textDim)}>```</div>
                  </div>
                </div>
              </div>
            </FeaturePanel>

            {/* Git Owned */}
            <FeaturePanel active={activeTab === 3}>
              <div className='grid lg:grid-cols-2 gap-12 items-center'>
                <div>
                  <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-xs text-orange-400 mb-6'>
                    ZERO LOCK-IN
                  </div>
                  <h3 className='text-3xl sm:text-4xl font-bold mb-6'>You Own the Repo</h3>
                  <p className={cn('text-lg mb-8 leading-relaxed', theme.textMuted)}>
                    Your documentation lives in Git. Full history, branch workflows, PR reviews for docs. If you ever
                    leave, you keep everything. Standard Markdown, no proprietary format.
                  </p>
                  <ul className='space-y-4'>
                    {[
                      'Full Git history for all changes',
                      'Branch and PR workflows for docs',
                      'Standard Markdown + extensions',
                      'Export anytime, no lock-in',
                    ].map((item) => (
                      <li key={item} className={cn('flex items-center gap-3', theme.textBody)}>
                        <LuGitBranch className='h-5 w-5 text-orange-400 shrink-0' />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className='relative'>
                  <div className='absolute -inset-4 bg-gradient-to-r from-orange-500/10 to-amber-500/10 rounded-2xl blur-2xl' />
                  <div
                    className={cn(
                      'relative rounded-xl border p-6 font-mono text-sm',
                      theme.codeBg,
                      theme.terminalBorder
                    )}>
                    <div className='flex items-center gap-2 text-orange-400 mb-4'>
                      <LuGitBranch className='h-4 w-4' />
                      <span>git log --oneline</span>
                    </div>
                    <div className={cn('space-y-2', theme.terminalText)}>
                      <div>
                        <span className='text-brand'>a3f2c1d</span> Update OAuth flow diagram
                      </div>
                      <div>
                        <span className='text-brand'>8b4e2a1</span> Add ADR-042: JWT strategy
                      </div>
                      <div>
                        <span className='text-brand'>c9d3f5e</span> Refactor auth architecture
                      </div>
                      <div>
                        <span className='text-brand'>2a1b8c4</span> Initial architecture docs
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </FeaturePanel>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className={cn('w-full h-px', theme.divider)} />

      {/* Comparison Section */}
      <section className='py-24 px-6 lg:px-8'>
        <div className='max-w-5xl mx-auto'>
          <div className='text-center mb-16'>
            <div
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs mb-6 border',
                isDarkMode ? 'bg-white/5 border-white/10 text-white/50' : 'bg-gray-100 border-gray-200 text-gray-500'
              )}>
              COMPARISON
            </div>
            <h2 className='text-4xl sm:text-5xl font-bold tracking-tight mb-4'>The honest comparison</h2>
            <p className={cn('text-lg max-w-2xl mx-auto', isDarkMode ? 'text-white/50' : 'text-gray-500')}>
              We're not trying to be everything. We're built for AI-native technical documentation.
            </p>
          </div>

          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead>
                <tr className={cn('border-b', isDarkMode ? 'border-white/10' : 'border-gray-200')}>
                  <th
                    className={cn(
                      'text-left py-5 px-4 font-medium',
                      isDarkMode ? 'text-white/50' : 'text-gray-500'
                    )}></th>
                  <th className='text-center py-5 px-4'>
                    <div className='inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand/10 text-brand font-semibold'>
                      Inovisum
                    </div>
                  </th>
                  <th
                    className={cn('text-center py-5 px-4 font-medium', isDarkMode ? 'text-white/40' : 'text-gray-400')}>
                    Notion
                  </th>
                  <th
                    className={cn('text-center py-5 px-4 font-medium', isDarkMode ? 'text-white/40' : 'text-gray-400')}>
                    Confluence
                  </th>
                  <th
                    className={cn('text-center py-5 px-4 font-medium', isDarkMode ? 'text-white/40' : 'text-gray-400')}>
                    GitBook
                  </th>
                </tr>
              </thead>
              <tbody className={cn('divide-y', isDarkMode ? 'divide-white/5' : 'divide-gray-100')}>
                <CompRow
                  feature='MCP Protocol (AI reads docs)'
                  values={[true, false, false, false]}
                  isDarkMode={isDarkMode}
                />
                <CompRow feature='Local LLM Support' values={[true, false, false, false]} isDarkMode={isDarkMode} />
                <CompRow feature='28 Diagram Formats' values={[true, false, false, '3']} isDarkMode={isDarkMode} />
                <CompRow feature='@AI Comment Review' values={[true, false, false, false]} isDarkMode={isDarkMode} />
                <CompRow feature='Git-Backed Storage' values={[true, false, false, true]} isDarkMode={isDarkMode} />
                <CompRow feature='Semantic Search' values={[true, true, false, 'basic']} isDarkMode={isDarkMode} />
                <CompRow feature='Real-time Collaboration' values={[true, true, true, false]} isDarkMode={isDarkMode} />
                <CompRow
                  feature='Data Sovereignty'
                  values={[true, false, 'self-host $$$', false]}
                  isDarkMode={isDarkMode}
                />
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id='security' className={cn('py-24 px-6 lg:px-8 scroll-mt-20', theme.sectionBgAlt)}>
        <div className='max-w-6xl mx-auto'>
          <div className='grid lg:grid-cols-2 gap-16 items-center'>
            <div>
              <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/10 border border-brand/20 text-xs text-brand mb-6'>
                <LuLock className='h-3 w-3' />
                SECURITY-FIRST
              </div>
              <h2 className='text-4xl sm:text-5xl font-bold tracking-tight mb-6'>
                Your architecture.
                <br />
                <span className={theme.textMuted}>Your infrastructure.</span>
              </h2>
              <p className={cn('text-lg mb-8 leading-relaxed', theme.textMuted)}>
                We know your architecture docs contain sensitive information. That's why we give you complete control
                over where your data lives and which AI models touch it.
              </p>
              <div className='space-y-6'>
                <SecurityFeature
                  title='Bring Your Own LLM'
                  description='Connect Ollama, vLLM, or any OpenAI-compatible endpoint. AI features work without sending data to external providers.'
                  isDarkMode={isDarkMode}
                />
                <SecurityFeature
                  title='Git-Native Storage'
                  description='Your docs live in a Git repository you control. Full history, full ownership, full portability.'
                  isDarkMode={isDarkMode}
                />
                <SecurityFeature
                  title='SSO & RBAC'
                  description='Enterprise-grade access control. SAML, OIDC, domain restrictions, role-based permissions.'
                  isDarkMode={isDarkMode}
                />
              </div>
            </div>
            <div className='relative'>
              <div className='absolute -inset-8 bg-gradient-to-r from-brand/10 via-primary/10 to-brand/10 rounded-3xl blur-3xl' />
              <div className={cn('relative rounded-2xl border p-8', theme.codeBg, theme.border)}>
                <div className='space-y-6'>
                  <div className={cn('flex items-center gap-4 p-4 rounded-xl border', theme.cardBg, theme.border)}>
                    <div className='p-3 rounded-lg bg-brand/10'>
                      <LuServer className='h-6 w-6 text-brand' />
                    </div>
                    <div>
                      <div className='font-medium mb-1'>Your Git Repository</div>
                      <div className={cn('text-sm', theme.textMuted)}>Full ownership, portable</div>
                    </div>
                    <LuCheck className='ml-auto h-5 w-5 text-brand' />
                  </div>
                  <div className={cn('flex items-center gap-4 p-4 rounded-xl border', theme.cardBg, theme.border)}>
                    <div className='p-3 rounded-lg bg-brand/10'>
                      <LuBrain className='h-6 w-6 text-brand' />
                    </div>
                    <div>
                      <div className='font-medium mb-1'>Your LLM (Ollama)</div>
                      <div className={cn('text-sm', theme.textMuted)}>Running locally</div>
                    </div>
                    <LuCheck className='ml-auto h-5 w-5 text-brand' />
                  </div>
                  <div className={cn('flex items-center gap-4 p-4 rounded-xl border', theme.cardBg, theme.border)}>
                    <div className='p-3 rounded-lg bg-violet-500/10'>
                      <LuLock className='h-6 w-6 text-violet-400' />
                    </div>
                    <div>
                      <div className='font-medium mb-1'>Zero Cloud Exposure</div>
                      <div className={cn('text-sm', theme.textMuted)}>Data never leaves</div>
                    </div>
                    <LuCheck className='ml-auto h-5 w-5 text-violet-400' />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className={cn('w-full h-px', theme.divider)} />

      {/* Pricing */}
      <section id='pricing' className='py-24 px-6 lg:px-8 scroll-mt-20'>
        <div className='max-w-4xl mx-auto'>
          <div className='text-center mb-16'>
            <div
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs mb-6 border',
                'bg-brand/10 border-brand/30 text-brand'
              )}>
              <LuSparkles className='h-3 w-3' />
              EARLY ACCESS
            </div>
            <h2 className='text-4xl sm:text-5xl font-bold tracking-tight mb-4'>
              Free during beta.
              <br />
              <span className={theme.textMuted}>All features unlocked.</span>
            </h2>
            <p className={cn('text-lg max-w-2xl mx-auto', theme.textMuted)}>
              Help us shape the future of AI-native documentation. Get full access while we're in beta.
            </p>
          </div>

          <div className='grid md:grid-cols-2 gap-8 items-start'>
            {/* Free Beta Card */}
            <div
              className={cn(
                'relative p-8 rounded-2xl border-2 transition-all',
                'border-brand/50 bg-brand/5 shadow-2xl shadow-brand/10'
              )}>
              <div className='absolute -top-3 left-1/2 -translate-x-1/2'>
                <span className='px-4 py-1 rounded-full bg-brand text-black text-xs font-semibold tracking-wide'>
                  FREE BETA
                </span>
              </div>

              <div className='text-center mb-8 pt-2'>
                <div className='text-lg font-medium mb-2'>Early Access</div>
                <div className='mb-2'>
                  <span className='text-5xl font-bold'>$0</span>
                </div>
                <div className={cn('text-sm', theme.textMuted)}>Full access while in beta</div>
              </div>

              <ul className='space-y-4 mb-8'>
                {[
                  'Personal organization included',
                  '1 additional organization on Free',
                  'Unlimited vaults per organization (storage-capped)',
                  'Storage cap enforced per organization',
                  'Monthly transcription minutes included',
                  'All diagram formats (28+)',
                  'Full MCP integration',
                  'Local LLM support via MCP',
                  'Semantic search',
                  'Private & public vaults',
                  'Git sync',
                ].map((feature) => (
                  <li
                    key={feature}
                    className={cn('flex items-center gap-3 text-sm', isDarkMode ? 'text-white/70' : 'text-gray-600')}>
                    <LuCheck className='h-4 w-4 text-brand shrink-0' />
                    {feature}
                  </li>
                ))}
              </ul>

              {isAuthenticated ? (
                <Link href={vaultsHref} className='block'>
                  <button
                    className={cn(
                      'w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all group',
                      isDarkMode ? 'bg-white text-black hover:bg-white/90' : 'bg-gray-900 text-white hover:bg-gray-800'
                    )}>
                    Go to your vaults
                    <LuArrowRight className='h-4 w-4 group-hover:translate-x-1 transition-transform' />
                  </button>
                </Link>
              ) : (
                <button
                  onClick={() => signIn()}
                  className={cn(
                    'w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all group',
                    isDarkMode ? 'bg-white text-black hover:bg-white/90' : 'bg-gray-900 text-white hover:bg-gray-800'
                  )}>
                  Start building for free
                  <LuArrowRight className='h-4 w-4 group-hover:translate-x-1 transition-transform' />
                </button>
              )}
            </div>

            {/* Coming Soon Card */}
            <div
              className={cn(
                'relative p-8 rounded-2xl border transition-all',
                isDarkMode ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-gray-50'
              )}>
              <div className='text-center mb-8'>
                <div className='text-lg font-medium mb-2'>Paid Plans</div>
                <div className='mb-4'>
                  <span className={cn('text-3xl font-bold', theme.textMuted)}>Coming Soon</span>
                </div>
                <div className={cn('text-sm', theme.textSubtle)}>After beta ends</div>
              </div>

              <div className={cn('space-y-4 mb-8', theme.textMuted)}>
                <p className='text-sm leading-relaxed'>
                  We're focused on building the best AI-native documentation platform. Once we're out of beta, we'll
                  introduce fair, transparent pricing.
                </p>
                <div className={cn('p-4 rounded-xl border', theme.cardBg, theme.border)}>
                  <div className='font-medium mb-2'>What to expect:</div>
                  <ul className='space-y-2 text-sm'>
                    <li className='flex items-center gap-2'>
                      <LuCheck className='h-3 w-3 text-brand' />
                      Generous free tier stays
                    </li>
                    <li className='flex items-center gap-2'>
                      <LuCheck className='h-3 w-3 text-brand' />
                      Team plans for larger orgs
                    </li>
                    <li className='flex items-center gap-2'>
                      <LuCheck className='h-3 w-3 text-brand' />
                      Enterprise with SSO & SLA
                    </li>
                  </ul>
                </div>
              </div>

              <Link href='/contact' className='block'>
                <Button
                  variant='outline'
                  className={cn(
                    'w-full h-12 text-base',
                    isDarkMode
                      ? 'border-white/20 hover:bg-white/10 hover:border-white/30'
                      : 'border-gray-300 hover:bg-gray-100 hover:border-gray-400'
                  )}>
                  Get notified when pricing launches
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className='py-32 px-6 lg:px-8 relative'>
        {/* Animated gradient background */}
        <div className='absolute inset-0'>
          <div className='absolute inset-0 bg-gradient-to-t from-brand/20 via-transparent to-transparent' />
          <div className='absolute bottom-0 left-1/4 w-[600px] h-[400px] bg-brand/30 rounded-full blur-[150px] opacity-40' />
          <div className='absolute bottom-0 right-1/4 w-[400px] h-[300px] bg-brand/30 rounded-full blur-[120px] opacity-30' />
        </div>

        <div className='relative max-w-4xl mx-auto text-center'>
          <h2 className='text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6'>
            Ready to bridge the gap
            <br />
            between docs and code?
          </h2>
          <p className={cn('text-xl mb-10 max-w-2xl mx-auto', theme.textMuted)}>
            Join engineering teams who've made their documentation an active part of their development workflow.
          </p>
          <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
            {isAuthenticated ? (
              <Link href={vaultsHref}>
                <button
                  className={cn(
                    'flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all group',
                    theme.buttonPrimary
                  )}>
                  Go to your vaults
                  <LuArrowRight className='h-4 w-4 group-hover:translate-x-1 transition-transform' />
                </button>
              </Link>
            ) : (
              <button
                onClick={() => signIn()}
                className={cn(
                  'flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all group',
                  theme.buttonPrimary
                )}>
                Get started free
                <LuArrowRight className='h-4 w-4 group-hover:translate-x-1 transition-transform' />
              </button>
            )}
            <Link href='/contact'>
              <button
                className={cn(
                  'flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all border',
                  theme.buttonOutline
                )}>
                Talk to us
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={cn('py-12 px-6 lg:px-8 border-t', theme.borderSubtle)}>
        <div className='max-w-6xl mx-auto'>
          <div className='flex flex-col md:flex-row items-center justify-between gap-6'>
            <div className='flex items-center gap-3'>
              <div className='w-8 h-8 rounded-lg bg-gradient-to-br from-brand to-brand flex items-center justify-center'>
                <span className='text-black font-bold text-xs'>IN</span>
              </div>
              <span className='font-semibold'>Inovisum</span>
              <span className={theme.textDim}>·</span>
              <span className={cn('text-sm', theme.textSubtle)}>Documentation for AI-native teams</span>
            </div>
            <div className={cn('flex items-center gap-6 text-sm', theme.textSubtle)}>
              <a href='#' className={cn('transition-colors', theme.hoverText)}>
                Privacy
              </a>
              <a href='#' className={cn('transition-colors', theme.hoverText)}>
                Terms
              </a>
              <Link href='/contact' className={cn('transition-colors', theme.hoverText)}>
                Contact
              </Link>
            </div>
          </div>
          <div className={cn('mt-8 pt-8 border-t text-center text-sm', theme.borderSubtle, theme.textDim)}>
            © {new Date().getFullYear()} Inovisum. All rights reserved.
          </div>
        </div>
      </footer>
    </main>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function FlowCard({
  step,
  icon,
  title,
  description,
  gradient,
  iconColor,
  highlight = false,
  isDarkMode,
}: {
  step: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  gradient: string;
  iconColor: string;
  highlight?: boolean;
  isDarkMode: boolean;
}) {
  return (
    <div
      className={cn(
        'relative p-8 rounded-2xl border transition-all',
        highlight
          ? 'border-brand/30 bg-brand/5'
          : isDarkMode
          ? 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20'
          : 'border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-gray-300'
      )}>
      {/* Step number */}
      <div className='absolute -top-3 left-8'>
        <span
          className={cn(
            'px-2 py-1 text-xs font-mono rounded border',
            isDarkMode ? 'text-white/30 bg-[#0a0a0b] border-white/10' : 'text-gray-400 bg-white border-gray-200'
          )}>
          {step}
        </span>
      </div>

      {/* Gradient background */}
      <div className={cn('absolute inset-0 rounded-2xl bg-gradient-to-br opacity-50 blur-xl', gradient)} />

      <div className='relative'>
        <div
          className={cn(
            'inline-flex items-center justify-center w-14 h-14 rounded-xl mb-6',
            isDarkMode ? 'bg-white/5' : 'bg-gray-100',
            iconColor
          )}>
          {icon}
        </div>
        <h3 className='text-xl font-semibold mb-3'>{title}</h3>
        <p className={cn('leading-relaxed', isDarkMode ? 'text-white/50' : 'text-gray-500')}>{description}</p>
      </div>

      {/* Arrow indicator for middle card on desktop */}
      {highlight && (
        <div className='hidden lg:flex absolute -right-6 top-1/2 -translate-y-1/2 z-10'>
          <LuChevronRight className='h-8 w-8 text-brand/50' />
        </div>
      )}
    </div>
  );
}

function FeaturePanel({ active, children }: { active: boolean; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'absolute inset-0 transition-all duration-500',
        active ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
      )}>
      {children}
    </div>
  );
}

function SecurityCard({ icon, label, isDarkMode }: { icon: React.ReactNode; label: string; isDarkMode: boolean }) {
  return (
    <div
      className={cn(
        'p-6 rounded-xl border text-center',
        isDarkMode ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-gray-50'
      )}>
      <div className='text-brand mb-3 flex justify-center'>{icon}</div>
      <div className={cn('text-sm', isDarkMode ? 'text-white/70' : 'text-gray-600')}>{label}</div>
    </div>
  );
}

function SecurityFeature({
  title,
  description,
  isDarkMode,
}: {
  title: string;
  description: string;
  isDarkMode: boolean;
}) {
  return (
    <div className='flex gap-4'>
      <div className='mt-1'>
        <LuCheck className='h-5 w-5 text-brand' />
      </div>
      <div>
        <div className='font-medium mb-1'>{title}</div>
        <div className={cn('text-sm', isDarkMode ? 'text-white/50' : 'text-gray-500')}>{description}</div>
      </div>
    </div>
  );
}

function CompRow({
  feature,
  values,
  isDarkMode,
}: {
  feature: string;
  values: (boolean | string)[];
  isDarkMode: boolean;
}) {
  return (
    <tr className='group'>
      <td
        className={cn(
          'py-4 px-4 text-left transition-colors',
          isDarkMode ? 'text-white/70 group-hover:text-white' : 'text-gray-600 group-hover:text-gray-900'
        )}>
        {feature}
      </td>
      {values.map((value, i) => (
        <td key={i} className='py-4 px-4 text-center'>
          {value === true ? (
            <LuCheck className='h-5 w-5 text-brand mx-auto' />
          ) : value === false ? (
            <span className={isDarkMode ? 'text-white/20' : 'text-gray-300'}>—</span>
          ) : (
            <span className={cn('text-xs', isDarkMode ? 'text-white/40' : 'text-gray-400')}>{value}</span>
          )}
        </td>
      ))}
    </tr>
  );
}
